import { useEffect, useState, useCallback } from 'react';
import api from './api';

/**
 * Fetch the public dynamic menu (admin-managed pages + menu items).
 * Returns { header: [...], footer: [...], loading, refetch }.
 * Each item: { id, label, href, link_type, open_in_new_tab, ... }
 *
 * Cached in module scope so repeated mounts (Header + Footer + page nav)
 * don't trigger separate network requests during the same session.
 */
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

const notify = () => { for (const fn of _subscribers) fn(_cache); };

export const fetchMenu = async (force = false) => {
  if (!force && _cache) return _cache;
  if (!force && _inflight) return _inflight;
  _inflight = api
    .get('/menu')
    .then((r) => {
      _cache = { header: r.data?.header || [], footer: r.data?.footer || [] };
      notify();
      return _cache;
    })
    .catch(() => {
      _cache = { header: [], footer: [] };
      notify();
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
};

export const invalidateMenuCache = () => { _cache = null; fetchMenu(true); };

export function useMenu() {
  const [data, setData] = useState(_cache || { header: [], footer: [] });
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let mounted = true;
    const sub = (val) => { if (mounted) setData(val || { header: [], footer: [] }); };
    _subscribers.add(sub);
    if (!_cache) {
      fetchMenu().then((val) => { if (mounted) { setData(val); setLoading(false); } });
    } else {
      setLoading(false);
    }
    return () => { mounted = false; _subscribers.delete(sub); };
  }, []);

  const refetch = useCallback(() => fetchMenu(true), []);
  return { header: data.header, footer: data.footer, loading, refetch };
}
